import { batchScalingEn, processRoutingEn } from './process-routing-en';

export const processRoutingFr = {
  ...processRoutingEn,
  title: 'Gamme de fabrication',
  subtitle:
    'Instructions, contrôles qualité, rendement et pertes versionnés pour cette version exacte de recette.',
  statuses: {
    DRAFT: 'Brouillon',
    IN_REVIEW: 'En vérification',
    APPROVED: 'Approuvée',
    BLOCKED: 'Bloquée',
    ARCHIVED: 'Archivée',
  },
  help: {
    ...processRoutingEn.help,
    open: 'Ouvrir l’aide sur les gammes',
    header:
      'Le numéro stable appartient à la recette ; chaque version de recette possède sa propre version contrôlée de gamme.',
    translations:
      'Les désignations et instructions sont disponibles dans toutes les langues prises en charge.',
    steps:
      'Ordonnez les étapes par glisser-déposer. Les facteurs décimaux sont calculés côté serveur sans virgule flottante.',
    history:
      'Chaque version reste liée à sa version de recette et devient immuable après soumission.',
    saveBeforeDocuments:
      'Enregistrez la gamme avant de charger documents et photos dans son dossier DMS protégé.',
    snapshotDocuments:
      'Les originaux protégés sélectionnés forment l’instantané documentaire immuable.',
  },
  actions: {
    reorder: 'Réordonner l’étape',
    scaleBatch: 'Dimensionner le lot',
    submit: 'Soumettre',
    review: 'Terminer la vérification',
    approve: 'Approuver',
    copy: 'Nouvelle version de gamme',
    block: 'Bloquer',
    archive: 'Archiver',
    addStep: 'Ajouter une étape',
    addByproduct: 'Ajouter un coproduit',
  },
  messages: {
    saved: 'Gamme enregistrée.',
    invalid: 'Corrigez les données ou structures JSON invalides.',
    submitted: 'Gamme soumise à une vérification indépendante.',
    workflowUpdated: 'Workflow de la gamme actualisé.',
  },
  sections: {
    header: 'En-tête de gamme',
    translations: 'Langues',
    steps: 'Étapes du processus',
    history: 'Historique des versions',
    documents: 'Dossier DMS de la gamme',
    snapshotDocuments: 'Instantané documentaire',
    stepTranslations: 'Instructions traduites',
    byproducts: 'Coproduits',
    stepDocuments: 'Documents et photos de l’étape',
  },
  fields: {
    ...processRoutingEn.fields,
    number: 'Numéro de gamme',
    name: 'Désignation',
    processFactor: 'Facteur global calculé',
    description: 'Description',
    localizedName: 'Désignation traduite',
    position: 'Position',
    stepName: 'Nom de l’étape',
    workplace: 'Poste / zone',
    input: 'Entrée',
    output: 'Sortie',
    yield: 'Rendement %',
    loss: 'Perte %',
    version: 'Version de gamme',
    recipeVersion: 'Version de recette',
    status: 'Statut',
    updated: 'Dernière modification',
    workCenter: 'Poste de travail',
    productionArea: 'Zone de production',
    inputQuantity: 'Quantité d’entrée',
    inputUnit: 'Unité d’entrée',
    outputQuantity: 'Quantité de sortie',
    outputUnit: 'Unité de sortie',
    targetTime: 'Temps cible (min)',
    setupTime: 'Réglage (min)',
    duration: 'Durée (min)',
    temperature: 'Température',
    temperatureUnit: 'Unité de température',
    scrap: 'Rebut %',
    absorption: 'Absorption %',
    instructions: 'Instructions de travail',
    parameters: 'Paramètres configurables (JSON)',
    qualityChecks: 'Contrôles qualité (JSON)',
    product: 'Article',
    document: 'Document',
    photo: 'Photo',
    reason: 'Motif / note traçable',
  },
  states: { noDocuments: 'Aucun document de processus protégé.' },
  stepDialog: {
    title: 'Modifier l’étape',
    description:
      'Gérer quantités, temps, paramètres, contrôles, pertes, instructions, coproduits et preuves.',
  },
  actionDialog: {
    REVIEW: {
      title: 'Terminer la vérification',
      description: 'Confirmer la vérification indépendante avec une note traçable.',
    },
    APPROVE: {
      title: 'Approuver la gamme',
      description:
        'L’approbation fige la gamme et impose une recette approuvée et le principe des quatre yeux.',
    },
    COPY: {
      title: 'Créer une nouvelle version',
      description: 'Copie cet instantané dans un nouveau brouillon avec lien de source.',
    },
    BLOCK: {
      title: 'Bloquer la gamme',
      description: 'La version reste immuable mais ne peut plus être utilisée.',
    },
    ARCHIVE: {
      title: 'Archiver la gamme',
      description: 'La version reste traçable et sort de l’utilisation active.',
    },
  },
} as const;

export const batchScalingFr = {
  ...batchScalingEn,
  title: 'Dimensionnement de lot',
  subtitle: 'Calcul matière reproductible côté serveur dans les deux directions.',
  help: {
    open: 'Ouvrir l’aide au dimensionnement',
    input: 'Choisissez recette approuvée, direction, unité de sortie et substitutions approuvées.',
    materials: 'Les besoins incluent pertes et sous-recettes et sont arrondis selon l’unité cible.',
    formulas: 'Chaque résultat intermédiaire et formule exacte du serveur est affiché.',
  },
  sections: {
    input: 'Données de calcul',
    materials: 'Besoins matière',
    limiting: 'Matières limitantes',
    subRecipes: 'Sous-recettes résolues',
    byproducts: 'Coproduits attendus',
    formulas: 'Formules et résultats intermédiaires',
    substitutions: 'Substitutions approuvées',
    available: 'Matières disponibles',
  },
  fields: {
    recipe: 'Version de recette',
    direction: 'Sens du calcul',
    outputUnit: 'Unité de sortie',
    position: 'Position {{position}}',
    primaryComponent: 'Utiliser le composant principal',
    targetQuantity: 'Quantité finale souhaitée',
    material: 'Matière',
  },
  directions: {
    TARGET_OUTPUT: 'Quantité finale → matières requises',
    AVAILABLE_INPUT: 'Matière disponible → quantité possible',
  },
  actions: { addMaterial: 'Ajouter une matière', calculate: 'Calculer le lot' },
  result: {
    output: 'Sortie possible / souhaitée',
    scale: 'Échelle de référence',
    processFactor: 'Facteur global',
    referenceOutput: 'Sortie de référence effective',
    level: 'Niveau BOM {{level}}',
  },
  columns: {
    article: 'Article',
    type: 'Type',
    quantity: 'Quantité requise',
    source: 'Chemins source',
    available: 'Disponible',
    requiredReference: 'Besoin de référence',
    possibleOutput: 'Sortie possible',
  },
  componentTypes: { MATERIAL: 'Matière', PACKAGING: 'Emballage' },
  states: { none: 'Aucun.' },
  messages: { invalid: 'Les données de calcul sont invalides.' },
  formulas: {
    PROCESS_STEP_FACTOR: 'Facteur d’étape',
    OVERALL_PROCESS_FACTOR: 'Facteur global',
    ROOT_RECIPE_SCALE: 'Échelle de la recette principale',
    COMPONENT_GROSS_REQUIREMENT: 'Besoin brut du composant',
    SUB_RECIPE_SCALE: 'Échelle de sous-recette',
    AVAILABLE_MATERIAL_OUTPUT: 'Sortie possible par matière',
  },
  warnings: {
    PROCESS_ROUTING_MISSING_ASSUMED_100_PERCENT:
      'Aucune gamme : un rendement de 100 % est supposé et doit être vérifié.',
    PROCESS_ROUTING_NOT_APPROVED: 'La gamme sélectionnée n’est pas approuvée.',
    STEP_YIELD_MISSING_ASSUMED_100_PERCENT:
      'Une étape n’a ni rendement ni données entrée/sortie ; 100 % est supposé.',
    STEP_YIELD_CONTRADICTS_INPUT_OUTPUT: 'Le rendement saisi contredit le rapport entrée/sortie.',
    RECIPE_VERSION_NOT_APPROVED: 'Une version de recette de la hiérarchie n’est pas approuvée.',
    COMPONENT_PRODUCT_BLOCKED_OR_ARCHIVED: 'Un article requis est bloqué ou arrêté.',
    MATERIAL_AVAILABILITY_NOT_PROVIDED:
      'Aucune disponibilité fournie pour cette matière ; elle ne limite pas le résultat.',
    AVAILABLE_MATERIAL_NOT_REQUIRED: 'Une matière fournie n’est pas requise.',
  },
} as const;
